import { useMemo, useState, useEffect } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { MdOutlineEdit } from 'react-icons/md';
import { v4 as uuidV4 } from 'uuid';
import TmdxAvatar from '../Avatar';
import { CustomAvatarProps } from '../Avatar/Avatar';
import { imageToBase64 } from '../../utils';
import IconButton from '../IconButton';

type TMDXFile = {
  id?: string;
  fileName: string;
  path: string;
  file?: File;
};

export interface AvatarInputProps extends Omit<DropzoneOptions, 'onDrop' | 'onError'> {
  src?: string | null;
  avatarProps?: Omit<CustomAvatarProps, 'src' | 'size' | 'radius' | 'isStatus'>;
  size?: CustomAvatarProps['size'];
  radius?: CustomAvatarProps['radius'];
  isBordered?: CustomAvatarProps['isBordered'];
  color?: CustomAvatarProps['color'];
  type?: CustomAvatarProps['type'];
  isInvalid?: boolean;
  name?: string | null;
  errorMessage?: string;
  onFileChange: (file: TMDXFile) => void;
}

const invalidFileTypeCode = 'file-invalid-type';

const AvatarInput = ({
  src,
  isInvalid,
  errorMessage,
  onFileChange,
  avatarProps,
  isBordered,
  color,
  type,
  size,
  radius,
  name,
  ...dropZoneProps
}: AvatarInputProps) => {
  const [avatarSrc, setAvatarSrc] = useState<string | undefined>(src ?? undefined);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setAvatarSrc(src ?? undefined);
  }, [src]);

  const validFileTypes = ['image/png', 'image/jpeg', 'image/heic'];
  const invalidFormatErrorMessage = 'Invalid file format. Please upload a PNG, JPEG, or HEIC file.';
  const MAX_FILE_SIZE = 15 * 1024 * 1024;

  const { getRootProps, getInputProps, open } = useDropzone({
    ...dropZoneProps,
    multiple: false,
    onDrop: async (acceptedFiles, fileRejections) => {
      if (acceptedFiles.length) {
        const image = acceptedFiles[0];

        if (!validFileTypes.includes(image.type)) {
          toast.error(invalidFormatErrorMessage);
          return;
        }

        if (image.size > MAX_FILE_SIZE) {
          toast.error('The file is too large. Please select a smaller file size (no larger than 15MB).');
          return;
        }

        const imageBase64 = await imageToBase64(image);

        setAvatarSrc(imageBase64);

        onFileChange({
          id: uuidV4(),
          fileName: image.name,
          path: image.name,
          file: image,
        });
      }

      if (fileRejections.length) {
        if (
          fileRejections.some((fileRejection) =>
            fileRejection.errors.some(({ code }) => code.includes(invalidFileTypeCode)),
          )
        ) {
          toast.error(invalidFormatErrorMessage);
          return;
        }

        const rejectedErrorSet = new Set(fileRejections.map((file) => file.errors.map((e) => e.message)).flat());

        setErrors(Array.from(rejectedErrorSet));
      } else {
        setErrors([]);
      }
    },
  });

  const errorMessages = useMemo(() => {
    return [errorMessage, ...errors].filter(Boolean).join(', ');
  }, [errors, errorMessage]);

  const invalid = isInvalid ?? !!errorMessages.length;

  return (
    <>
      <div
        {...getRootProps({
          className: `dropzone relative inline-flex ${dropZoneProps.disabled ? 'pointer-events-none' : 'cursor-pointer'}`,
        })}
      >
        <input {...getInputProps()} />

        <TmdxAvatar
          isBordered={isBordered}
          src={avatarSrc}
          radius={radius}
          color={color}
          size={size}
          type={type}
          name={name}
          {...avatarProps}
        />

        {!dropZoneProps.disabled && (
          <div
            className={`${radius === 'full' ? 'absolute bottom-[-0.4rem] right-2' : 'absolute -bottom-1.5 right-px'}`}
          >
            <IconButton
              onPress={open}
              startContent={<MdOutlineEdit size={16} />}
              variant="solid"
              size="xs"
              className="bg-base-10"
            />
          </div>
        )}
      </div>

      {invalid && <span className="text-tiny text-danger">{errorMessages}</span>}
    </>
  );
};

export default AvatarInput;
